import { http } from '@/lib/http';
import type { Page } from '@/types/common';
import type {
  Vendor,
  VendorAddress,
  VendorAddressRole,
  VendorStatus,
} from '@/types/vendors';

const BASE = '/api/v1/vendors';

export interface VendorListQuery {
  search?: string;
  status?: VendorStatus;
  page?: number;
  pageSize?: number;
}

export interface VendorCreatePayload {
  name: string;
  code?: string;
  email?: string;
  phone?: string;
  gstin?: string;
  pan?: string;
  externalRef?: string;
  status?: VendorStatus;
}

export type VendorUpdatePayload = Partial<Omit<VendorCreatePayload, 'code'>>;

export interface VendorAddressCreatePayload {
  label?: string;
  role?: VendorAddressRole;
  contactName?: string;
  contactPhone?: string;
  gstin?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode?: string;
  country?: string;
  isDefaultBilling?: boolean;
  isDefaultShipping?: boolean;
}

export type VendorAddressUpdatePayload = Partial<VendorAddressCreatePayload>;

export const vendorsService = {
  list: (query: VendorListQuery = {}) =>
    http.get<Page<Vendor>>(BASE, { params: query }),
  get: (id: string) => http.get<Vendor>(`${BASE}/${id}`),
  create: (payload: VendorCreatePayload) =>
    http.post<Vendor, VendorCreatePayload>(BASE, payload),
  update: (id: string, payload: VendorUpdatePayload) =>
    http.patch<Vendor, VendorUpdatePayload>(`${BASE}/${id}`, payload),
  archive: (id: string) => http.delete<void>(`${BASE}/${id}`),

  // Addresses
  listAddresses: (vendorId: string) =>
    http.get<VendorAddress[]>(`${BASE}/${vendorId}/addresses`),
  createAddress: (vendorId: string, payload: VendorAddressCreatePayload) =>
    http.post<VendorAddress, VendorAddressCreatePayload>(
      `${BASE}/${vendorId}/addresses`,
      payload,
    ),
  updateAddress: (
    vendorId: string,
    addressId: string,
    payload: VendorAddressUpdatePayload,
  ) =>
    http.patch<VendorAddress, VendorAddressUpdatePayload>(
      `${BASE}/${vendorId}/addresses/${addressId}`,
      payload,
    ),
  deleteAddress: (vendorId: string, addressId: string) =>
    http.delete<void>(`${BASE}/${vendorId}/addresses/${addressId}`),
};
